import { LitElement, html } from "lit";
import jquery from "jquery";
import parsley from "parsleyjs";

class FitVoucher extends LitElement {
  render() {
    return html` <slot name="content"></slot> `;
  }

  static get is() {
    return "fit-voucher";
  }

  static get properties() {
    return {
      accountHolder: {
        type: String,
        notify: true,
      },
      accountIBAN: {
        type: String,
        notify: true,
      },
      AGBAccepted: {
        type: Boolean,
        notify: true,
      },
      amount: {
        type: Number,
        notify: true,
      },
      buyerCity: {
        type: String,
        notify: true,
      },
      buyerCompany: {
        type: String,
        notify: true,
      },
      buyerCountry: {
        type: String,
        notify: true,
      },
      buyerEmail: {
        type: String,
        notify: true,
      },
      buyerFamilyName: {
        type: String,
        notify: true,
      },
      buyerName: {
        type: String,
        notify: true,
      },
      buyerPostalCode: {
        type: String,
        notify: true,
      },
      buyerStreet: {
        type: String,
        notify: true,
      },
      buyerTelephone: {
        type: String,
        notify: true,
      },
      buyerTitle: {
        type: String,
        notify: true,
      },
      charsLeft: {
        type: Number,
        notify: true,
      },
      customAmount: {
        type: Number,
      },
      // finalAmount isn't the amount to pay. it's the amount after checking if normal or custom input value, without fee
      finalAmount: {
        type: Number,
        notify: true,
      },
      // finalAmountToPay is the final amount + the payment method fee
      finalAmountToPay: {
        type: Number,
        notify: true,
      },
      imageData: {
        type: String,
        notify: true,
      },
      imageFileTypes: {
        type: Array,
      },
      imageSizeLimit: {
        type: Number,
      },
      linesHeights: {
        type: Array,
      },
      linesLeft: {
        type: Number,
        notify: true,
      },
      message: {
        type: String,
        notify: true,
      },
      paymentFee: {
        type: Number,
        notify: true,
      },
      paymentMethod: {
        type: String,
        notify: true,
      },
      selectedBackground: {
        type: String,
        notify: true,
      },
      uploadImageDisabled: {
        type: Boolean,
        notify: true,
      },
      voucherCode: {
        type: String,
      },
      withPhoto: {
        type: Boolean,
        notify: true,
      },
    };
  }

  constructor() {
    super();

    this.AGBAccepted = false;
    this.amount = 0;
    this.charsLeft = 0;
    this.finalAmount = 0;
    this.imageFileTypes = ["image/jpeg", "image/png", "image/bmp", "image/gif"];
    this.imagesSizeLimit = 10500000;
    this.linesHeights = [0, 16, 38, 60, 82, 104, 126, 148, 170, 192, 214];
    this.linesLeft = 0;
    this.message = "";
    this.paymentFee = 0;
    this.selectedBackground =
      "https://images.pexels.com/photos/808465/pexels-photo-808465.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260";
    this.voucherCode = "XXX-XXX";
    this.withPhoto = true;

    // Default values for the buyer form
    this.buyerTitle = "n/a";
    this.buyerCompany = "n/a";
    this.buyerName = "n/a";
    this.buyerFamilyName = "n/a";
    this.buyerStreet = "n/a";
    this.buyerEmail = "n/a";
    this.buyerPostalCode = "n/a";
    this.buyerCity = "n/a";
    this.buyerTelephone = "n/a";
    this.buyerCountry = "n/a";
  }

  connectedCallback() {
    super.connectedCallback();
    this._filterPaymentMethods();
    this.addListeners();
    this._init();
    this._initializeParsley();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Add Event Listeners
   */
  addListeners() {
    const that = this;

    // Slider Tabs
    document.querySelectorAll(".voucher-slider__tab").forEach((sliderTab) => {
      sliderTab.addEventListener("click", (event) => {
        that._handleTabClick(event);
      });
    });

    // Slider Images
    document
      .querySelectorAll(".voucher-slider__image")
      .forEach((sliderImage) => {
        sliderImage.addEventListener("click", () => {
          that._handleThumbnailClick(event);
        });
      });

    // Upload Button
    document
      .querySelector("#imageUploadInput")
      .addEventListener("change", (event) => {
        that._uploadImage(event);
      });

    // No image Button
    document
      .querySelector("#withPhotoCheckbox")
      .addEventListener("change", () => {
        that._toggleWithPhotoState();
      });

    // Text area
    const txtArea = document.querySelector("#messageInput");
    txtArea.addEventListener("paste", (event) => {
      event.preventDefault();
      document
        .querySelector("#pasteError")
        .classList.add("voucher-textarea__paste-error--visible");
      txtArea.style.border = "1px solid #ec5840";
    });
    txtArea.addEventListener("input", () => {
      that._handleMessageChange();
      document
        .querySelector("#pasteError")
        .classList.remove("voucher-textarea__paste-error--visible");
      txtArea.style.border = "1px solid #111111";
    });

    // Normal Value Inputs
    document
      .querySelectorAll(".voucher-value__button-input--simple")
      .forEach((normalValueInput) => {
        normalValueInput.addEventListener("click", (event) => {
          that._handleAmountClick(event);
        });
      });

    // Custom Value Input
    const customInputField = document.querySelector(
      ".voucher-value__custom-input"
    );
    customInputField.addEventListener("input", () => {
      that._handleCustomAmountButtonInput();
    });
    customInputField.addEventListener("blur", () => {
      that._handleCustomAmountBlur();
    });
    customInputField.addEventListener("change", () => {
      that._handleCustomAmountChange();
    });

    // Buyer Contact Info Inputs
    document
      .querySelectorAll(".voucher-contact-info__input")
      .forEach((buyerInput) => {
        buyerInput.addEventListener("input", (event) => {
          that._handleBuyerFormChange(event);
        });
      });
    document
      .querySelectorAll(".voucher-contact-info__input--title")
      .forEach((buyerInputTitle) => {
        buyerInputTitle.addEventListener("input", (event) => {
          that._handleBuyerTitleClick(event);
        });
      });

    // Country Input Field
    document
      .querySelector("#buyerCountry")
      .addEventListener("change", (event) => {
        that._handleCountryChange(event);
      });

    // Payment Click
    document
      .querySelectorAll(".voucher-payments__input")
      .forEach((paymentType) => {
        paymentType.addEventListener("click", (event) => {
          that._handlePaymentMethodClick(event);
          that._showFinalAmount();
        });
      });

    // Payment extra input (lastschrift)
    document
      .querySelectorAll(".voucher-payments__payment-extra-input")
      .forEach((paymentExtraInput) => {
        paymentExtraInput.addEventListener("input", (event) => {
          that._savePaymentFormData(event);
        });
      });

    // Preview Toggle
    document
      .querySelector(".voucher-preview__preview-toggle")
      .addEventListener("click", () => {
        that._togglePreview();
      });

    // 'Vorschau' click
    document
      .querySelector(".voucher-preview__preview-title")
      .addEventListener("click", () => {
        that._handleVorschauWordClick();
      });

    // AGB checkbox
    document
      .querySelector("#acceptConditions")
      .addEventListener("click", (event) => {
        that._saveVoucherDataProperty("AGBAccepted", event.target.checked);
      });
  }

  /**
   * Initialize all needed elements
   */
  _init() {
    const textArea = document.querySelector("#messageInput");
    const previewMessage = document.querySelector("#previewMessage");

    textArea.value = this.message;
    previewMessage.textContent = this.message;

    this._setVoucherBackgroundImage();
    this._handleMessageChange();
    this._showFinalAmount();
  }

  /**
   * Initialize parsley (JS Validation Library)
   * exclude the nested input on the custom amount
   */
  _initializeParsley() {
    jquery("#voucherForm").parsley({
      excluded: "input.voucher-value__custom-input",
      errorsWrapper: '<ul class="input__errors"></ul>',
      errorTemplate: '<li class="input__error"></li>',
    });
  }

  /**
   * Slider Tab Click
   * Select the current active Slider tab button and toggle all necessary classes
   */
  _handleTabClick(event) {
    event.preventDefault();
    document
      .querySelector(".voucher-slider__tab--active")
      .classList.remove("voucher-slider__tab--active");
    document
      .querySelector(".voucher-slider__slider--visible")
      .classList.remove("voucher-slider__slider--visible");
    event.currentTarget.classList.add("voucher-slider__tab--active");

    document
      .querySelectorAll(".voucher-slider__slider")
      .forEach((newContent) => {
        if (
          newContent.getAttribute("data-content-name") ===
          event.currentTarget.getAttribute("data-open-content")
        ) {
          newContent.classList.add("voucher-slider__slider--visible");
        }
      });
  }

  /**
   * Slider image click
   */
  // todo: TEST WHEN THE FIT-SLIDER ELEMENT IS WORKING
  _handleThumbnailClick(event) {
    const previousThumb = document.querySelector(
      ".voucher-slider__image--selected"
    );

    previousThumb &&
      previousThumb.classList.remove("voucher-slider__image--selected");

    event.currentTarget.classList.add("voucher-slider__image--selected");

    this.selectedBackground = event.currentTarget.src;

    var selectedBackgroundValue = "";
    if (!event.currentTarget.hasAttribute("data-val")) {
      // old logic
      selectedBackgroundValue = event.currentTarget.src;
    } else {
      // new logic
      selectedBackgroundValue = event.currentTarget.dataset.val;
    }

    const selectedBackground = document.querySelector("#selectedBackground");
    if (selectedBackground) {
      selectedBackground.value = selectedBackgroundValue;
    }

    this._setVoucherBackgroundImage();
  }

  /**
   * Set the background image on the gibfcard preview
   */
  _setVoucherBackgroundImage() {
    document.querySelector("#preview__bg-image").src = this.selectedBackground;
  }

  /**
   * Click on button to toggle between show/hide uploaded image
   */
  _toggleWithPhotoState() {
    this.uploadImageDisabled = !this.uploadImageDisabled;

    const previewRoundImage = document.querySelector("#previewImageElements");
    this.withPhoto = document.querySelector("#withPhotoCheckbox").checked;
    this.withPhoto === true
      ? (previewRoundImage.style.display = "block")
      : (previewRoundImage.style.display = "none");

    const uploadLabel = document.querySelector(".voucher-upload__label");
    uploadLabel.classList.toggle("voucher-upload__label--neutral");

    const uploadButton = document.querySelector(".voucher-upload__input");
    uploadButton.disabled = !this.uploadImageDisabled;
  }

  /**
   * Check file tzpe, file size upload image and save imageData
   */
  _uploadImage(event) {
    this._toggleSpinnerState("show");

    const imageInput = event.target;
    if (imageInput.files && imageInput.files[0]) {
      const fileType = imageInput.files[0].type;

      // check for file type
      if (this.imageFileTypes.indexOf(fileType) >= 0) {
        // check for file size
        if (imageInput.files[0].size < this.imagesSizeLimit) {
          const reader = new FileReader();
          reader.onload = (event) => {
            this.imageData = event.target.result;
            document.getElementById(
              "previewImageHolder"
            ).style.backgroundImage = `url('${this.imageData}')`;
            this._toggleSpinnerState("hide");
          };
          reader.readAsDataURL(imageInput.files[0]);
          this._showUploadNotification("Success");
        } else {
          this._showUploadNotification("Size");
        }
      } else {
        this._showUploadNotification("File");
      }
    }
  }

  /**
   * Toggle the spinner state so it is visible while the photo is uploading
   */
  _toggleSpinnerState(newState) {
    const spinnerContainer = document.querySelector("#imageSpinnerContainer");
    if (null === spinnerContainer) {
      return;
    }
    spinnerContainer.classList.toggle(
      "voucher-spinner--show",
      newState === "show"
    );
  }

  /**
   * Display correct notification when image is uploaded
   */
  _showUploadNotification(notification) {
    document
      .querySelectorAll(".voucher-upload__notification")
      .forEach((uploadNotification) => {
        uploadNotification.classList.remove(
          "voucher-upload__notification--show"
        );
      });

    document
      .querySelector(`#uploadNotification${notification}`)
      .classList.add("voucher-upload__notification--show");
  }

  /**
   * Input on message Area
   * Set Message and Textarea styles according to message length
   */
  _handleMessageChange() {
    const textArea = document.querySelector("#messageInput");
    const newMessage = textArea.value;
    const previewMessage = document.querySelector("#previewMessage");
    const charLeftMessage = document.querySelector("#charsLeftError");
    const charsLeft = document.querySelector("#charsLeftNumber");

    this.charsLeft = newMessage.length;
    this.message = newMessage;

    charsLeft.textContent = this.charsLeft;
    previewMessage.textContent = this.message;
    if (this.charsLeft < 450) {
      textArea.style.border = "1px solid #cccccc";
      charLeftMessage.style.color = "#111111";
    } else if (this.charsLeft >= 450 && this.charsLeft < 500) {
      textArea.style.border = "1px solid #f9b900";
      charLeftMessage.style.color = "#f9b900";
    } else if (this.charsLeft >= 500) {
      textArea.style.border = "1px solid #ec5840";
      charLeftMessage.style.color = "#ec5840";
    }
    this._checkMessageRows();
  }

  /**
   * Limit the message to 9 Rows in the voucher
   * (not in the textarea itself, but in the preview and pdf voucher)
   */
  _checkMessageRows() {
    const textArea = document.querySelector("#messageInput");
    const linesLeftMessage = document.querySelector("#linesLeftError");
    const previewHeight =
      document.querySelector("#previewMessage").offsetHeight;
    const linesLeft = document.querySelector("#linesLeft");

    for (let i = 0; i < this.linesHeights.length; i++) {
      if (previewHeight === this.linesHeights[i]) {
        this.linesLeft = i;
      }
    }

    if (this.linesLeft < 7) {
      textArea.style.border = "1px solid #cccccc";
      linesLeftMessage.style.color = "#111111";
    } else if (this.linesLeft >= 7 && this.linesLeft < 9) {
      textArea.style.border = "1px solid #f9b900";
      linesLeftMessage.style.color = "#f9b900";
    } else if (this.linesLeft === 9) {
      textArea.style.border = "1px solid #ec5840";
      linesLeftMessage.style.color = "#ec5840";
    } else if (this.linesLeft >= 9) {
      this.message = this.message.slice(0, -2);
    }

    if (this.linesLeft > 9) {
      document.execCommand("undo");
      this.linesLeft = 9;
    }

    textArea.value = this.message;
    linesLeft.innerText = this.linesLeft;
  }

  /**
   * Click on one of the amount buttons
   */
  _handleAmountClick(event) {
    event.cancelBubble;

    const clickedLabel = event.currentTarget.nextElementSibling;
    const clickedAmount = clickedLabel
      .querySelector(".voucher-value__button")
      .getAttribute("value");

    this.amount = clickedAmount;
    this.finalAmount = this.amount;
    this._updateCustomAmountField();
    this._addAmountButtonStyle(clickedLabel);
    this._showFinalAmount();
  }

  /**
   * Set style on clicked Amount button
   */
  _addAmountButtonStyle(clickedLabel) {
    if (document.querySelector(".voucher-value__button-container--selected")) {
      document
        .querySelector(".voucher-value__button-container--selected")
        .classList.remove("voucher-value__button-container--selected");
    }

    if (document.querySelector(".voucher-value__custom-input--selected")) {
      document
        .querySelector(".voucher-value__custom-input--selected")
        .classList.remove("voucher-value__custom-input--selected");
    }

    if (document.querySelector(".voucher-value__custom-input--warning")) {
      document
        .querySelector(".voucher-value__custom-input--warning")
        .classList.remove("voucher-value__custom-input--warning");
    }

    clickedLabel.parentNode.classList.add(
      "voucher-value__button-container--selected"
    );
  }

  /**
   * Enter custom Amount. Set custom amount limit
   */
  _handleCustomAmountButtonInput() {
    const customAmountInput = document.querySelector(
      ".voucher-value__custom-input"
    );
    let customAmountValue = customAmountInput.value;
    const maxLength = 5;

    if (customAmountValue.length > maxLength) {
      customAmountValue = customAmountValue.slice(0, maxLength);
      customAmountInput.value = customAmountValue;
    }

    if (customAmountValue < 20) {
      document
        .querySelector(".voucher-value__notification")
        .classList.add("voucher-value__notification--show");
      customAmountInput.classList.add("voucher-value__custom-input--warning");
    } else {
      document
        .querySelector(".voucher-value__notification")
        .classList.remove("voucher-value__notification--show");
      customAmountInput.classList.remove(
        "voucher-value__custom-input--warning"
      );
    }

    if (customAmountValue > 0) {
      document
        .querySelector(".voucher-value__input-tips")
        .classList.add("voucher-value__input-tips--show");
      customAmountInput.classList.add("voucher-value__custom-input--selected");
    } else {
      document
        .querySelector(".voucher-value__input-tips")
        .classList.remove("voucher-value__input-tips--show");
    }

    this.customAmount = customAmountValue;
    this.finalAmount = this.customAmount;

    this._handleCustomAmountChange();
    this._showFinalAmount();
  }

  /**
   * Check for custom amount value on blur
   */
  _handleCustomAmountBlur() {
    const customAmountInput = document.querySelector(
      ".voucher-value__custom-input"
    );
    const customAmountValue = customAmountInput.value;

    if (customAmountValue < 20) {
      this.finalAmount = 0;
      this._showFinalAmount();
      customAmountInput.value = "";
      document
        .querySelector(".voucher-value__input-tips")
        .classList.remove("voucher-value__input-tips--show");
      document
        .querySelector(".voucher-value__notification")
        .classList.remove("voucher-value__notification--show");
    }
  }

  /**
   * On custom amount value change we should delesect the radio buttons
   */
  _handleCustomAmountChange() {
    if (document.querySelector(".voucher-value__button-input:checked")) {
      document
        .querySelector(".voucher-value__button-input:checked")
        .parentNode.classList.remove(
          "voucher-value__button-container--selected"
        );
      document.querySelector(
        ".voucher-value__button-input:checked"
      ).checked = false;
    }
    document.getElementById("amount6").checked = true;
  }

  /**
   * Update the amount field to reflect the amount value
   */
  _updateCustomAmountField() {
    document.querySelector("#voucherValueCustomInput").value = this.amount;
  }

  /**
   * Display final amount on the voucher preview
   */
  _showFinalAmount() {
    const orderVoucherAmount = document.querySelector("#orderVoucherAmount");
    orderVoucherAmount.textContent = parseInt(this.finalAmount).toFixed(2);
    document.querySelector("#finalAmountContainer").textContent =
      this.finalAmount;

    const feeValue = this._calculateCardFee();
    const finalAmountWithFee = parseInt(this.finalAmount) + feeValue;
    this.finalAmountToPay = finalAmountWithFee;
    const orderAmountFee = document.querySelector("#orderVoucherFee");
    const orderAmountToPay = document.querySelector("#orderAmountToPay");
    orderAmountFee.textContent = feeValue.toFixed(2);
    orderAmountToPay.textContent = finalAmountWithFee.toFixed(2);
  }

  /**
   * Calculate Card Fee according to selected payment method
   */
  _calculateCardFee() {
    if (this.paymentMethod) {
      const feeAmount = document.getElementById(this.paymentMethod).dataset.fee;
      this.paymentFee = this.finalAmount * (feeAmount / 100);
    }
    return this.paymentFee;
  }

  /**
   * Save the buyer title to the voucher properties
   */
  _handleBuyerTitleClick(event) {
    this._saveVoucherDataProperty(
      "buyerTitle",
      event.target.getAttribute("id")
    );
  }

  /**
   * Show small tips on the inputs fields
   * when the placeholder isn't visible anymore
   */
  _handleBuyerFormChange(event) {
    const currentInput = event.target;
    const currentTip = event.target.nextElementSibling;

    currentInput.value.length
      ? currentTip.classList.add("voucher-contact-info__input-tips--show")
      : currentTip.classList.remove("voucher-contact-info__input-tips--show");

    this._saveVoucherDataProperty(
      currentInput.getAttribute("id"),
      currentInput.value
    );
  }

  /**
   *   Country Select Change
   *   run the "Handle Customer Country" function each time the Country is changed
   */
  _handleCountryChange(event) {
    this._filterPaymentMethods();
    this._saveVoucherDataProperty("buyerCountry", event.target.value);
  }

  /**
   * Filter and show the correct payment methods
   * according to the selected country
   */
  _filterPaymentMethods() {
    const customerCountryField = document.querySelector("#buyerCountry");
    const elementsOnlyAvailableInSomeCountries = document.querySelectorAll(
      '[available-in-country]:not([available-in-country=""])'
    );

    if (customerCountryField && elementsOnlyAvailableInSomeCountries.length) {
      const selectedCountryPattern = new RegExp(
        `(^|,)${customerCountryField.value}($|,)`
      );

      elementsOnlyAvailableInSomeCountries.forEach((element) => {
        const availableInCountryList = element.getAttribute(
          "available-in-country"
        );
        if (selectedCountryPattern.exec(availableInCountryList) === null) {
          element.hidden = true;
          const selected = element.querySelector(".radio:checked");
          if (selected) {
            selected.checked = false;
          }
        } else {
          element.hidden = false;
        }
      });
    }
  }

  /**
   * Set payment method
   */
  _handlePaymentMethodClick(event) {
    if (document.querySelector(".voucher-payments__payment-type--expanded")) {
      document
        .querySelector(".voucher-payments__payment-type--expanded")
        .classList.remove("voucher-payments__payment-type--expanded");
    }
    if (document.querySelector(".voucher-payments__payment-type--selected")) {
      document
        .querySelector(".voucher-payments__payment-type--selected")
        .classList.remove("voucher-payments__payment-type--selected");
    }

    const paymentContainer = event.target.parentNode;
    paymentContainer.classList.add("voucher-payments__payment-type--selected");
    if (paymentContainer.id === "debitCardsPaymentType") {
      paymentContainer.classList.add(
        "voucher-payments__payment-type--expanded"
      );
    }
    this.paymentMethod = event.target.id;

    const extraInputFields = document.querySelectorAll(
      ".voucher-payments__payment-extra-input"
    );
    if (this.paymentMethod === "Debit") {
      extraInputFields.forEach((field) => {
        field.required = true;
      });
    } else {
      extraInputFields.forEach((field) => {
        field.required = false;
      });
    }

    this._saveVoucherDataProperty(
      event.target.getAttribute("id"),
      event.target.getAttribute("id")
    );
  }

  /**
   * Save value of payment fields
   */
  _savePaymentFormData(event) {
    this._saveVoucherDataProperty(
      event.target.getAttribute("id"),
      event.target.value
    );
  }

  /**
   * Toggle the Preview on and off on smaller screens
   */
  _togglePreview() {
    const previewToggle = document.querySelector(
      ".voucher-preview__preview-toggle"
    );
    const rightColumn = document.querySelector(".voucher-preview");

    previewToggle.classList.toggle(
      "voucher-preview__preview-toggle--point-down"
    );
    rightColumn.classList.toggle("voucher-preview--show");
  }

  /**
   * On mobile, toggle preview on "vorschau" word click
   */
  _handleVorschauWordClick() {
    if (window.innerWidth < 1020) {
      this._togglePreview();
    }
  }

  /**
   * Set value of changed buyer input in the local properties
   */
  _saveVoucherDataProperty(inputName, newValue) {
    this[inputName] = newValue;
    // this._logData()
  }

  /**
   * console log the voucher data
   * useful for debugging purposes
   */
  _logData() {
    const _logData = {
      Title: this.buyerTitle,
      Company: this.buyerCompany,
      Name: this.buyerName,
      FamilyName: this.buyerFamilyName,
      Street: this.buyerStreet,
      Email: this.buyerEmail,
      PostalCode: this.buyerPostalCode,
      City: this.buyerCity,
      Telephone: this.buyerTelephone,
      Country: this.buyerCountry,
      AccountHolder: this.accountHolder,
      IBAN: this.accountIBAN,
      PaymentMethod: this.paymentMethod,
      withPhoto: this.withPhoto,
      photoBgd: this.selectedBackground,
      imageData: this.imageData,
      message: this.message,
      amount: this.amount,
      customAmount: this.customAmount,
      finalAmount: this.finalAmount,
      finalAmountToPay: this.finalAmountToPay,
      voucherCode: this.voucherCode,
      ABG: this.AGBAccepted,
    };
    console.table(_logData);
  }
}

customElements.define(FitVoucher.is, FitVoucher);
